
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FilmInventory } from "@/types/inventory";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TransferFilmForm } from "./TransferFilmForm";
import { toast } from "sonner";
import { z } from "zod";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

// Define the schema for the transfer form
const transferFormSchema = z.object({
  quantity: z.number().min(1, { message: "Must transfer at least 1 roll" }),
  notes: z.string().optional(),
});

// Define the type from the schema
type TransferFormValues = z.infer<typeof transferFormSchema>;

interface TransferFilmDialogProps {
  film: FilmInventory;
  onComplete: () => void;
  trigger?: React.ReactNode;
}

export function TransferFilmDialog({ film, onComplete, trigger }: TransferFilmDialogProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t, language } = useTranslation();

  // Initialize with default values
  const form = useForm<TransferFormValues>({
    resolver: zodResolver(transferFormSchema),
    defaultValues: {
      quantity: 1,
      notes: "",
    },
  });

  const handleSubmit = async (values: TransferFormValues) => {
    try {
      if (values.quantity > film.quantity) {
        toast.error(`${t('only_have')} ${film.quantity} ${t('rolls_of_this_film')}`);
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast.error(t('must_be_signed_in_transfer'));
        return;
      }

      // 1. Get the current film details
      const { data: currentFilm, error: fetchError } = await supabase
        .from('film_inventory')
        .select('*')
        .eq('id', film.id)
        .single();

      if (fetchError || !currentFilm) {
        throw new Error(fetchError?.message || t('failed_fetch_film_details'));
      }

      // 2. Check if quantity is still valid (in case it was changed elsewhere)
      if (values.quantity > currentFilm.quantity) {
        toast.error(`${t('only_have')} ${currentFilm.quantity} ${t('rolls_of_this_film_now')}`);
        return;
      }

      // 3. Calculate remaining quantity
      const remainingQuantity = currentFilm.quantity - values.quantity;

      // Using a transaction to ensure data consistency
      if (remainingQuantity > 0) {
        // Update existing record if there's remaining film
        const { error: updateError } = await supabase
          .from('film_inventory')
          .update({ quantity: remainingQuantity })
          .eq('id', film.id);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Delete the record if no film remains
        const { error: deleteError } = await supabase
          .from('film_inventory')
          .delete()
          .eq('id', film.id);

        if (deleteError) {
          throw deleteError;
        }
      }

      // 4. Create a new record for the exposed film
      // Using type assertion to bypass TypeScript's strict checking
      const { error: insertError } = await supabase
        .from('film_inventory')
        .insert({
          film_name: film.film_name,
          film_type: film.film_type,
          status: "exposed", // Set status to exposed
          expiration_date: film.expiration_date,
          iso: film.iso,
          brand: film.brand,
          quantity: values.quantity,
          notes: values.notes || `${t('transferred_from_fresh')} ${new Date().toLocaleDateString()}`,
          user_id: userData.user.id,
        } as any);

      if (insertError) {
        throw insertError;
      }

      toast.success(`${values.quantity} ${t('rolls_marked_exposed')}`);
      setOpen(false);
      form.reset();
      onComplete();
    } catch (error: any) {
      console.error("Error transferring film:", error);
      toast.error(`${t('error')}: ${error.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw] p-4" : "max-w-md"}>
        <DialogHeader>
          <DialogTitle>{t('mark_film_exposed')}</DialogTitle>
          <DialogDescription>
            {t('transfer_film_description')}
          </DialogDescription>
        </DialogHeader>
        <TransferFilmForm 
          film={film} 
          form={form} 
          onSubmit={handleSubmit} 
        />
      </DialogContent>
    </Dialog>
  );
}
